// Espace du responsable technique (D-093) : ce que partagent ses écrans — la casquette, le club de
// la saison (équipes, équipes engagées, coachs, effectifs, niveaux), les pastilles, les blocs d'exercice.
import './rt.css';
import {
  rtCoaches, rtFfbbLinks, rtMembers, rtPrepare, rtSeason, rtSquads, rtTeams,
  type RtCoach, type RtMember, type RtSeason, type RtSquad, type RtTeam,
} from '../lib/rt-api';
import type { ClubBrand } from '../lib/api';
import { localDate } from '../domain/schedule';
import { CATEGORY_LABEL, type Category } from '../domain/session';
import { ageGroup, levelFromCompetition, teamBadge, type AgeGroup, type SessionState } from '../domain/rt';
import { stepSvg } from '../domain/court';
import type { Ctx } from './context';
import { html, raw, type Html } from './dom';

/** La casquette « Resp. technique » est affichée (choisie, ou la seule). */
export const isRtView = (c: Ctx) => c.hat === 'technique' || (c.hats.length === 1 && c.hats[0]?.key === 'technique');

export const today = () => localDate(new Date());
export const COURT = html`<svg class="hero-court" viewBox="0 0 220 220" fill="none" aria-hidden="true"><circle cx="110" cy="110" r="100"/><path d="M10 110h200M110 10c-40 30-40 170 0 200M110 10c40 30 40 170 0 200"/></svg>`;
export const ico = (n: string) => html`<span class="ti ${n}" aria-hidden="true"></span>`;
export const pctTxt = (v: number | null) => (v === null ? '–' : `${v} %`);
export const CAT_COLOR: Record<Category, string> = {
  echauffement: '#E0A526', technique: '#2B5FB8', tactique: '#6D4BC2', physique: '#C2410C', jeu: '#1D7A5C', retour_calme: '#7A6E64',
};
export const badge = (k: string, sm = false) => html`<span class="rt-badge ${sm ? 'sm' : ''}">${k}</span>`;
const S_CLS: Record<SessionState | 'none', string> = { plan: 'ok', noplan: 'np', cancelled: 'ko', upcoming: 'up', none: 'no' };
export const stateCls = (s: SessionState | 'none') => S_CLS[s];
export const dots = (states: SessionState[]) => html`<span class="rt-dots">${states.map(s => html`<i class="rt-s ${S_CLS[s]}"></i>`)}</span>`;
/** Les segments d'un onglet (liens). */
export const seg = (items: [string, string][], on: number) =>
  html`<nav class="seg bk-seg rt-seg" aria-label="Sections">${items.map(([l, href], i) => html`<a href="${href}" ${i === on ? raw('aria-current="page"') : ''}>${l}</a>`)}</nav>`;

/** Vignette d'un exercice : son schéma, sinon la couleur de sa catégorie. */
export function thumb(diagram: unknown, category: Category): Html {
  const svg = diagram ? stepSvg(diagram as never, 0, 'slice') : null;
  return html`<span class="sc-thumb">${svg ? raw(svg) : html`<span class="sc-ico" style="background:${CAT_COLOR[category] ?? '#7A6E64'}"></span>`}</span>`;
}
/** Un exercice en lecture (bloc de séance de l'app) ; `extra` : bouton à droite. */
export const exRow = (x: { title: string; category: Category; minutes: number | null; diagram?: unknown; sub?: string }, extra: Html | '' = '') => html`
  <div class="sc-item rt-exo" style="--cat:${CAT_COLOR[x.category] ?? '#7A6E64'}"><div class="sc-main">${thumb(x.diagram, x.category)}
    <span class="sc-txt"><strong>${x.title}</strong><small>${x.sub ?? `${CATEGORY_LABEL[x.category] ?? ''}${x.minutes ? ` · ${x.minutes} min` : ''}`}</small></span>${extra}</div></div>`;

/** Une équipe vue par le responsable : un groupe, ou une de ses équipes engagées (SF1, SF2…). */
export interface Unit { key: string; team: RtTeam; squad: RtSquad | null; badge: string; name: string; level: string | null; category: string | null; group: AgeGroup | null }

export interface RtClub {
  club: ClubBrand; season: RtSeason; today: string;
  teams: RtTeam[]; squads: RtSquad[]; coaches: RtCoach[]; members: RtMember[];
  units: Unit[];
  /** Coachs d'une équipe : le principal d'abord. */
  coachesOf: (teamId: string) => RtCoach[];
  coachLine: (teamId: string) => string;
  badgeOf: (teamId: string) => string;
  nameOf: (userId: string) => string;
}

/** Le club de la saison, tel que le voit le responsable. Prépare la saison au passage (cycles, modèle d'obligations). */
export async function loadRtClub(ctx: Ctx): Promise<RtClub | null> {
  const club = ctx.club;
  if (!club) return null;
  await rtPrepare(club.id).catch(() => null);
  const season = await rtSeason(club.id);
  if (!season) return null;
  const teams = await rtTeams(club.id, season.id);
  const ids = teams.map(t => t.id);
  const [squads, coaches, members, links] = await Promise.all([rtSquads(ids), rtCoaches(club.id).catch(() => [] as RtCoach[]), rtMembers(ids), rtFfbbLinks(ids).catch(() => [])]);
  const levelOf = (t: RtTeam, q: RtSquad | null): string | null => {
    if (q?.level) return q.level;
    const fromSquad = q?.engagement_id ? levelFromCompetition(links.find(l => l.team_id === t.id && l.engagement_id === q.engagement_id)?.competition_name) : null;
    return fromSquad ?? t.level ?? levelFromCompetition(links.find(l => l.team_id === t.id && l.competition_type === 'championnat')?.competition_name ?? links.find(l => l.team_id === t.id)?.competition_name);
  };
  const units: Unit[] = teams.flatMap((t): Unit[] => {
    const qs = squads.filter(q => q.team_id === t.id);
    const base = { team: t, category: t.category, group: ageGroup(t.category) };
    return qs.length > 1
      ? qs.map(q => ({ ...base, key: `${t.id}:${q.id}`, squad: q, badge: teamBadge(q.name, q.name.length <= 6 ? q.name : null), name: q.name, level: levelOf(t, q) }))
      : [{ ...base, key: t.id, squad: null, badge: teamBadge(t.name, t.short_name), name: t.name, level: levelOf(t, qs[0] ?? null) }];
  });
  const coachesOf = (teamId: string) => coaches.filter(c => c.team_id === teamId).sort((a, b) => (a.role === b.role ? a.name.localeCompare(b.name) : a.role === 'head_coach' ? -1 : 1));
  return {
    club, season, today: today(), teams, squads, coaches, members, units, coachesOf,
    coachLine: (teamId) => coachesOf(teamId).map(c => c.name).join(', ') || 'sans coach',
    badgeOf: (teamId) => { const t = teams.find(x => x.id === teamId); return t ? teamBadge(t.name, t.short_name) : '·'; },
    nameOf: (userId) => coaches.find(c => c.user_id === userId)?.name ?? 'un coach',
  };
}

/** Écran d'attente du responsable : pas de club ou pas de saison en cours. */
export const noSeason = html`<div class="empty"><p class="empty-title">Pas de saison en cours.</p><p>Le bureau ouvre la saison et ses équipes : l'espace du responsable technique se remplit ensuite.</p></div>`;
